// src/components/PublicProfilePage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "../axios";
import BackLink from "./BackLink";
import AdCard from "./AdCard";
import "./PublicProfilePage.css";
import AdUnavailable from "./AdUnavailable";

const getColorFromName = (name = "") => {
  const colors = ["#ffadad", "#ffd6a5", "#caffbf", "#9bf6ff", "#bdb2ff", "#ffc6ff"];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return colors[sum % colors.length];
};

const PublicProfilePage = () => {
  const { profileId } = useParams();
  const location = useLocation();

  // ⭐ пришёл ли пользователь из уведомлений о новых объявлениях
  const params = new URLSearchParams(location.search);

  const highlightNewAds = params.get("highlight") === "newAds";
  const highlightPriceChanged = params.get("highlight") === "priceChanged";
  const focusAdId = parseInt(params.get("focusAd"), 10);

  // для новых объявлений
  const highlightAdIds = new Set(
    (params.get("adIds") || "")
      .split(",")
      .map((x) => parseInt(x, 10))
      .filter((n) => Number.isFinite(n))
  );

  // для завершённых объявлений
  const highlightClosedIds = new Set(
    (params.get("highlightIds") || "")
      .split(",")
      .map((x) => parseInt(x, 10))
      .filter((n) => Number.isFinite(n))
  );   

  const [profile, setProfile] = useState(null);
  const [activeAds, setActiveAds] = useState([]);
  const [closedAds, setClosedAds] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [animateClosedTab, setAnimateClosedTab] = useState(false);
  const [missingAd, setMissingAd] = useState(false);  
  const [loading, setLoading] = useState(true);
  // Infinite scroll (front-only)
  const PAGE_SIZE = 20;

  const [visibleActive, setVisibleActive] = useState(PAGE_SIZE);
  const [visibleClosed, setVisibleClosed] = useState(PAGE_SIZE);

  const sentinelRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const firstHighlightedRef = useRef(null);
  // underline for tabs (Avito effect)
  const tabsRef = useRef(null);
  const tabActiveRef = useRef(null);
  const tabClosedRef = useRef(null);

  const moveUnderlineToEl = (btnEl) => {
    const tabs = tabsRef.current;
    if (!tabs || !btnEl) return;

    const tabsRect = tabs.getBoundingClientRect();
    const btnRect = btnEl.getBoundingClientRect();

    tabs.style.setProperty("--u-left", `${btnRect.left - tabsRect.left}px`);
    tabs.style.setProperty("--u-width", `${btnRect.width}px`);
  };

  // follow
  const [isFollowing, setIsFollowing] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  const token = localStorage.getItem("token");

  const displayName =
    (profile?.name && profile.name.trim()) ||
    profile?.email ||
    profile?.phone ||
    "Πωλητής";

  const initial = (displayName || "?").charAt(0).toUpperCase();

  // ⚠️ ЭНДПОИНТЫ:
  // 1) Публичный профиль: GET /api/public-profiles/<id>/
  // 2) Объявления продавца: GET /api/ads/?profile=<id>
  // 3) Статус подписки: GET /api/profiles/<profileId>/is-following/
  // 4) Подписаться / отписаться: POST /api/profiles/<profileId>/follow/
  //  

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const pRes = await axios.get(`/api/profiles/${profileId}/public/`);

        setProfile(pRes.data?.profile || null);
        setFollowersCount(pRes.data?.profile?.followers_count || 0);
        setActiveAds(pRes.data?.active_ads || []);
        setClosedAds(pRes.data?.closed_ads || []);
        const params = new URLSearchParams(location.search);
        const tabParam = params.get("tab");

        const adIdsFromUrl = [
          ...highlightAdIds,
          ...highlightClosedIds
        ];

        let shouldOpenTab = "active";

        const activeIds = new Set((pRes.data?.active_ads || []).map(a => a.id));
        const closedIds = new Set((pRes.data?.closed_ads || []).map(a => a.id));
        // ⭐ проверяем удалено ли объявление
        if (
          Number.isFinite(focusAdId) &&
          !activeIds.has(focusAdId) &&
          !closedIds.has(focusAdId)
        ) {
          setMissingAd(true);
        }
        let shouldAnimateClosedTab = false;

        // клик по конкретному объявлению
        if (Number.isFinite(focusAdId)) {

          if (closedIds.has(focusAdId)) {
            shouldAnimateClosedTab = true;
          }

        }

        // клик по общему уведомлению
        else if (highlightNewAds && adIdsFromUrl.length > 0) {

          const hasClosed = adIdsFromUrl.some(id => closedIds.has(id));

          if (hasClosed) {
            shouldAnimateClosedTab = true;
          }

        }

        setAnimateClosedTab(shouldAnimateClosedTab);

        // ⭐ если кликнули конкретное объявление
        if (Number.isFinite(focusAdId)) {

          if (activeIds.has(focusAdId)) {
            shouldOpenTab = "active";
          } else if (closedIds.has(focusAdId)) {
            shouldOpenTab = "closed";
          }

        }

        // обычная логика уведомлений
        else if (tabParam === "closed") {
          shouldOpenTab = "closed";

        } else if (adIdsFromUrl.length > 0) {

          const hasActive = adIdsFromUrl.some(id => activeIds.has(id));
          const hasClosed = adIdsFromUrl.some(id => closedIds.has(id));

          if (hasActive) {
            shouldOpenTab = "active";
          } else if (hasClosed) {
            shouldOpenTab = "closed";
          }

        }

        setActiveTab(shouldOpenTab);

        // статус follow (только если авторизован)
        if (token) {
          const sRes = await axios.get(`/api/profiles/${profileId}/is-following/`);
          setIsFollowing(!!sRes.data?.is_following);
        }
      } catch (e) {
        console.error("Σφάλμα φόρτωσης δημόσιου προφίλ:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [profileId]); // eslint-disable-line  

  useEffect(() => {
    // при смене вкладки начинаем снова с первых PAGE_SIZE карточек
    if (activeTab === "active") setVisibleActive(PAGE_SIZE);
    if (activeTab === "closed") setVisibleClosed(PAGE_SIZE);
  }, [activeTab]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;

        if (isLoadingMoreRef.current) return;
        isLoadingMoreRef.current = true;

        const list = activeTab === "active" ? activeAds : closedAds;

        if (activeTab === "active") {
          setVisibleActive((v) => Math.min(v + PAGE_SIZE, list.length));
        } else {
          setVisibleClosed((v) => Math.min(v + PAGE_SIZE, list.length));
        }

        setTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 0);
      },
      { root: null, rootMargin: "250px", threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, activeAds, closedAds]);

  const handleFollowClick = async () => {
    if (!token) {
      setShowPopover(true);
      return;
    }

    try {
      const res = await axios.post(`/api/profiles/${profileId}/follow/`);
      setIsFollowing(!!res.data?.is_following);
    } catch (e) {
      console.error("Σφάλμα follow:", e);
      alert("Σφάλμα. Προσπαθήστε ξανά.");
    }
    const statsRes = await axios.get(`/api/profiles/${profileId}/follow-stats/`);
    setFollowersCount(statsRes.data?.followers_count || 0);
  };

  const openLogin = () => {
    setShowPopover(false);
    window.dispatchEvent(new Event("open-login-modal"));
  };

  // ✅ underline: встань под активную вкладку + пересчёт при resize
useEffect(() => {
  // куда ставить underline? -> на активную кнопку
  const activeEl = activeTab === "active" ? tabActiveRef.current : tabClosedRef.current;
  moveUnderlineToEl(activeEl);

  const onResize = () => {
    const activeNow = activeTab === "active" ? tabActiveRef.current : tabClosedRef.current;
    moveUnderlineToEl(activeNow);
  };

  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, [activeTab, activeAds.length, closedAds.length]);

// ⭐ автоскролл к новым объявлениям из уведомления и завершеным
useEffect(() => {
  const hasNewHighlight =
    highlightNewAds && highlightAdIds.size > 0;

  const hasPriceChangedHighlight =
    highlightPriceChanged && highlightAdIds.size > 0;

  const hasClosedHighlight =
    activeTab === "closed" && highlightClosedIds.size > 0;

  if (!hasNewHighlight && !hasPriceChangedHighlight && !hasClosedHighlight) return;
  if (!firstHighlightedRef.current) return;

  setTimeout(() => {
    firstHighlightedRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }, 400);

}, [
  loading,
  activeTab,
  highlightNewAds,
  highlightPriceChanged,
  highlightAdIds.size,
  highlightClosedIds.size,
]);

  if (loading) {
    return (
      <div className="public-profile-page">
        <BackLink label="Πίσω" />
        <div className="profile-empty">Φόρτωση...</div>
      </div>
    );
  }

  if (missingAd) {
    return <AdUnavailable />;
  }

  if (!profile) {
    return (
      <div className="public-profile-page">
        <BackLink label="Πίσω" />
        <div className="profile-empty">Το προφίλ δεν βρέθηκε.</div>
      </div>
    );
  }  

  return (
    <div className="public-profile-page">
      <BackLink label="Πίσω" />

      <div className="seller-card">
        <div className="seller-left">
          {profile.avatar ? (
            <img className="seller-avatar" src={profile.avatar} alt="avatar" />
          ) : (
            <div
              className="seller-avatar placeholder"
              style={{ backgroundColor: getColorFromName(displayName) }}
            >
              {initial}
            </div>
          )}

          <div className="seller-meta">
            <div className="seller-name">{displayName}</div>
            {profile.created_at && (
              <div className="seller-sub">
                Μέλος από: {new Date(profile.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        <div className="seller-sub">
          Ακόλουθοι: {followersCount}
        </div>

        <div className="seller-actions">
          <div className="follow-wrap">
            <button
              type="button"
              className={isFollowing ? "btn btn-outline" : "btn btn-primary"}
              onClick={handleFollowClick}
            >
              {isFollowing ? "Ακολουθείτε" : "Ακολούθησε"}
            </button>

            {showPopover && (
              <div className="auth-popover" onMouseLeave={() => setShowPopover(false)}>
                <div className="auth-popover-title">Χρειάζεται σύνδεση</div>
                <div className="auth-popover-text">
                  Συνδεθείτε για να ακολουθήσετε αυτόν τον πωλητή.
                </div>
                <button className="btn btn-primary w-100" onClick={openLogin}>
                  Είσοδος / Εγγραφή
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <h2 className="seller-ads-title">Αγγελίες πωλητή</h2>

      {/* Tabs */}
      <div
        className="profile-tabs"
        ref={tabsRef}
        onMouseLeave={() => {
          // когда ушли мышкой с вкладок — вернуться на активную
          const activeEl = activeTab === "active" ? tabActiveRef.current : tabClosedRef.current;
          moveUnderlineToEl(activeEl);
        }}
      >
        <button
          type="button"
          ref={tabActiveRef}
          className={`profile-tab ${activeTab === "active" ? "is-active" : ""}`}
          onClick={() => setActiveTab("active")}
          onMouseEnter={(e) => moveUnderlineToEl(e.currentTarget)} // hover -> уезжает сюда
        >
          Ενεργές <span className="profile-tab-count">{activeAds.length}</span>
        </button>

        <button
          type="button"
          ref={tabClosedRef}
          className={`profile-tab 
            ${activeTab === "closed" ? "is-active" : ""} 
            ${animateClosedTab ? "tab-attention" : ""}
          `}
          onClick={() => setActiveTab("closed")}
          onMouseEnter={(e) => moveUnderlineToEl(e.currentTarget)} // hover -> уезжает сюда
        >
          Ολοκληρωμένες <span className="profile-tab-count">{closedAds.length}</span>
        </button>

        {/* underline */}
        <div className="profile-tab-underline" />
      </div>

      {/* Ads list */}
      {(() => {
        const list = activeTab === "active" ? activeAds : closedAds;
        const visible = activeTab === "active" ? visibleActive : visibleClosed;
        const shown = list.slice(0, visible);

        if (list.length === 0) {
          return (
            <div className="profile-empty">
              Δεν υπάρχουν αγγελίες.
            </div>
          );
        }

        return (
          <>
            <div className="profile-ads-grid">
              {shown.map((ad, index) => {
                const isHighlighted =
                  Number.isFinite(focusAdId)
                    ? ad.id === focusAdId
                    : (
                        (highlightNewAds && highlightAdIds.has(ad.id)) ||
                        (highlightPriceChanged && highlightAdIds.has(ad.id)) ||
                        (activeTab === "closed" && highlightClosedIds.has(ad.id))
                      );      

                return (
                  <AdCard 
                    key={ad.id}
                    ad={ad}
                    highlight={isHighlighted}
                    highlightRef={
                      (
                        (Number.isFinite(focusAdId) && ad.id === focusAdId) ||
                        (!Number.isFinite(focusAdId) && isHighlighted)
                      ) && !firstHighlightedRef.current
                        ? firstHighlightedRef
                        : null
                    }
                  />
                );
              })}
            </div>

            {/* “ловушка” внизу списка */}
            <div ref={sentinelRef} className="profile-sentinel" />

            {/* если ещё есть что докидывать — показываем подпись */}
            {visible < list.length && (
              <div className="profile-loading-more">
                Φόρτωση...
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default PublicProfilePage;
